import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Skeleton } from '~/components/ui/skeleton';

/**
 * Inline overlay rendered while the resume flow fetches the draft + its
 * blocking prereqs. Mirrors the wizard chrome (header band + step indicator
 * + form area) so the page reflows minimally when the real wizard mounts.
 *
 * - 15s soft timeout → "still loading" toast + Cancel link to go back.
 * - Hard error path is owned by the loader (toast + redirect on `error`).
 *   This component is purely the loading-state visual.
 */
export function ResumeOverlay() {
  const { t } = useTranslation('experiments');
  const navigate = useNavigate();
  const [showSlow, setShowSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSlow(true);
      toast.message(t('resume.slow'), {
        action: {
          label: t('resume.cancel'),
          onClick: () => {
            void navigate({ to: '/experiments' });
          },
        },
      });
    }, 15_000);
    return () => {
      clearTimeout(timer);
    };
  }, [navigate, t]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('resume.loading')}
      className="bg-gradient-page flex h-full min-h-0 flex-col overflow-hidden"
    >
      {/* Stepper band */}
      <div className="shrink-0 px-6 py-5">
        <div className="flex items-center gap-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-8 flex-1 rounded-full" />
          ))}
        </div>
      </div>

      {/* Body — title + form card */}
      <div className="flex-1 overflow-y-auto px-10 py-6">
        <div className="space-y-3 text-center">
          <Skeleton className="mx-auto h-7 w-72" />
          <Skeleton className="mx-auto h-4 w-96" />
          {showSlow && <p className="text-muted-foreground pt-2 text-xs">{t('resume.slow')}</p>}
        </div>

        <div className="mt-8 rounded-xl border bg-white p-6">
          <div className="grid grid-cols-3 gap-6">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-6">
            {Array.from({ length: 2 }, (_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-xl border bg-white p-6">
          <Skeleton className="h-5 w-48" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-between border-t bg-white/60 px-6 py-4">
        <Skeleton className="h-9 w-9" />
        <Skeleton className="h-9 w-24" />
      </div>
    </div>
  );
}
